import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Check, Lock, Sparkles, Info, Clock, Square, CheckSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { API } from '@/App';
import Navigation from '@/components/Navigation';
import weekHeroImages from '@/data/weekHeroImages';
import ShareAchievementModal from '@/components/ShareAchievementModal';

// Daily checklist component with custom tasks
function DailyChecklist({ weekNumber, dayNumber }) {
  const [items, setItems] = useState([]);
  const [checked, setChecked] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTask, setEditingTask] = useState(null);
  const [editValue, setEditValue] = useState('');

  useEffect(() => {
    fetchChecklist();
  }, [weekNumber, dayNumber]);

  const fetchChecklist = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/checklist/${weekNumber}/${dayNumber}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setItems(response.data.items || []);
      setChecked(response.data.checked || []);
    } catch (error) {
      console.error('Failed to fetch checklist:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleItem = async (itemId) => {
    // Don't toggle if it's a custom task that needs setup
    const item = items.find(i => i.id === itemId);
    if (item?.needs_setup) {
      setEditingTask(item.task_number);
      setEditValue('');
      return;
    }

    const isChecked = checked.includes(itemId);
    const newChecked = isChecked 
      ? checked.filter(id => id !== itemId)
      : [...checked, itemId];
    
    setChecked(newChecked);

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/checklist/update`, {
        week: parseInt(weekNumber),
        day: dayNumber,
        item_id: itemId,
        checked: !isChecked
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      // Revert on error
      setChecked(checked);
      toast.error('Failed to update checklist');
    }
  };

  const saveCustomTask = async (taskNumber) => {
    if (!editValue.trim()) {
      toast.error('Please enter a task description');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/checklist/custom-task`, {
        task_number: taskNumber,
        task_label: editValue.trim()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Custom essential saved!');
      setEditingTask(null);
      setEditValue('');
      fetchChecklist(); // Refresh to show updated task
    } catch (error) {
      toast.error('Failed to save custom task');
    }
  };

  if (loading) {
    return (
      <div className="bg-gradient-to-br from-primary/5 to-accent/5 rounded-xl p-4 border border-primary/20">
        <p className="text-sm text-gray-500 text-center">Loading checklist...</p>
      </div>
    );
  }

  const completedCount = checked.length;
  const totalCount = items.length;
  const progressPercent = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;
  const customTaskCount = items.filter(i => i.is_custom).length;

  return (
    <div className="bg-gradient-to-br from-primary/5 to-accent/5 rounded-xl p-5 border-2 border-primary/20 mb-4" data-testid={`checklist-day-${dayNumber}`}>
      <div className="flex items-center justify-between mb-2">
        <h4 className="font-semibold text-secondary-900 flex items-center gap-2">
          <CheckSquare className="w-5 h-5 text-primary" />
          Daily Essentials Checklist
        </h4>
        <span className="text-sm font-medium text-primary">
          {completedCount}/{totalCount} completed
        </span>
      </div>
      
      {/* Info about growing checklist */}
      <p className="text-xs text-secondary-600 mb-3">
        Week {weekNumber}: 7 core essentials + {customTaskCount} personal essential{customTaskCount !== 1 ? 's' : ''} 
        {weekNumber < 12 && <span className="text-primary"> • Add 1 new each week → 20 total by Week 12!</span>}
      </p>
      
      {/* Progress bar */}
      <div className="h-2 bg-gray-200 rounded-full mb-4 overflow-hidden">
        <div 
          className="h-full bg-gradient-to-r from-primary to-accent transition-all duration-500 ease-out rounded-full"
          style={{ width: `${progressPercent}%` }}
        />
      </div>

      <div className="space-y-2">
        {/* Core essentials */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {items.filter(item => !item.is_custom).map((item) => {
            const isItemChecked = checked.includes(item.id);
            return (
              <button
                key={item.id}
                onClick={() => toggleItem(item.id)}
                className={`flex items-center gap-3 p-3 rounded-lg text-left transition-all duration-200 ${
                  isItemChecked 
                    ? 'bg-green-100 border-2 border-green-300 text-green-800' 
                    : 'bg-white border-2 border-gray-200 hover:border-primary/50 text-secondary-700'
                }`}
                data-testid={`checklist-item-${item.id}`}
              >
                {isItemChecked ? (
                  <CheckSquare className="w-5 h-5 text-green-600 flex-shrink-0" />
                ) : (
                  <Square className="w-5 h-5 text-gray-400 flex-shrink-0" />
                )}
                <span className={`text-sm font-medium ${isItemChecked ? 'line-through' : ''}`}>
                  {item.label}
                </span>
              </button>
            );
          })}
        </div>

        {/* Custom essentials section */}
        {items.some(item => item.is_custom) && (
          <div className="mt-4 pt-4 border-t border-primary/20">
            <h5 className="text-sm font-semibold text-secondary-800 mb-2 flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-accent" />
              My Personal Essentials
            </h5>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {items.filter(item => item.is_custom).map((item) => {
                const isItemChecked = checked.includes(item.id);
                const isEditing = editingTask === item.task_number;
                const isDay7 = dayNumber === 7;
                
                if (isEditing || item.needs_setup) {
                  // Only show "add essential" prompt on day 7
                  if (!isDay7 && item.needs_setup && !isEditing) {
                    return null; // Hide on days 1-6
                  }
                  return (
                    <div 
                      key={item.id}
                      className="p-3 rounded-lg bg-amber-50 border-2 border-amber-300 border-dashed"
                    >
                      {isEditing ? (
                        <div className="space-y-2">
                          <input
                            type="text"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            placeholder={`Enter your essential #${item.task_number}...`}
                            className="w-full px-3 py-2 text-sm border border-amber-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                            autoFocus
                          />
                          <div className="flex gap-2">
                            <button
                              onClick={() => saveCustomTask(item.task_number)}
                              className="flex-1 px-3 py-1.5 bg-primary text-white text-sm rounded-lg hover:bg-primary/90"
                            >
                              Save
                            </button>
                            <button
                              onClick={() => { setEditingTask(null); setEditValue(''); }}
                              className="px-3 py-1.5 bg-gray-200 text-gray-700 text-sm rounded-lg hover:bg-gray-300"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => { setEditingTask(item.task_number); setEditValue(''); }}
                          className="w-full flex items-center gap-3 text-left text-amber-700"
                        >
                          <Info className="w-5 h-5 flex-shrink-0" />
                          <span className="text-sm font-medium">Out of this week's challenges, choose your most important and add it to your daily essentials</span>
                        </button>
                      )}
                    </div>
                  );
                }
                
                return (
                  <button
                    key={item.id}
                    onClick={() => toggleItem(item.id)}
                    className={`flex items-center gap-3 p-3 rounded-lg text-left transition-all duration-200 ${
                      isItemChecked 
                        ? 'bg-green-100 border-2 border-green-300 text-green-800' 
                        : 'bg-amber-50 border-2 border-amber-200 hover:border-primary/50 text-secondary-700'
                    }`}
                    data-testid={`checklist-item-${item.id}`}
                  >
                    {isItemChecked ? (
                      <CheckSquare className="w-5 h-5 text-green-600 flex-shrink-0" />
                    ) : (
                      <Square className="w-5 h-5 text-amber-500 flex-shrink-0" />
                    )}
                    <span className={`text-sm font-medium ${isItemChecked ? 'line-through' : ''}`}>
                      {item.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function WeekView() {
  const { weekNumber } = useParams();
  const navigate = useNavigate();
  const [week, setWeek] = useState(null);
  const [currentDay, setCurrentDay] = useState(null);
  const [currentWeek, setCurrentWeek] = useState(null);
  const [canCompleteToday, setCanCompleteToday] = useState(true);
  const [nextUnlockDate, setNextUnlockDate] = useState(null);
  const [programComplete, setProgramComplete] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [completingTask, setCompletingTask] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [completedTaskInfo, setCompletedTaskInfo] = useState({ week: 1, day: 1 });

  useEffect(() => {
    fetchWeekData();
  }, [weekNumber]);

  const fetchWeekData = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const [weekRes, progressRes] = await Promise.all([
        axios.get(`${API}/weeks/${weekNumber}`, config),
        axios.get(`${API}/user/progress`, config)
      ]);

      setWeek(weekRes.data);
      setCurrentDay(progressRes.data.current_day);
      setCurrentWeek(progressRes.data.current_week);
      setCanCompleteToday(progressRes.data.can_complete_today);
      setNextUnlockDate(progressRes.data.next_unlock_date);
      setProgramComplete(progressRes.data.program_complete || false);
      setIsAdmin(progressRes.data.is_admin || false);
      setLoading(false);
      
      // Program complete users can still browse all weeks - no redirect needed
    } catch (error) {
      toast.error('Failed to load week data');
      navigate('/dashboard');
    }
  };

  const handleCompleteTask = async (task) => {
    if (task.locked) {
      toast.error(`Complete Day ${currentDay} first before moving forward!`);
      return;
    }

    setCompletingTask(true);

    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const response = await axios.post(
        `${API}/tasks/complete`,
        {
          task_id: task.id,
          week: parseInt(weekNumber),
          day: task.day
        },
        config
      );

      // Check if completing Day 7 (end of week) - trigger confetti on dashboard
      if (task.day === 7) {
        localStorage.setItem('showWeekConfetti', 'true');
        localStorage.setItem('completedWeek', weekNumber);
        
        // Check if this is week 12 (program complete!)
        if (parseInt(weekNumber) === 12) {
          localStorage.setItem('programComplete', 'true');
          toast.success(`🎉 Congratulations! You've completed the entire Radiant 40 program! 🎉`, {
            duration: 5000,
          });
        } else {
          toast.success(`🎉 Week ${weekNumber} complete! You're amazing!`, {
            duration: 4000,
          });
        }
      } else {
        toast.success(`Day ${task.day} completed! Watch your garden grow! 🌸`, {
          duration: 3000,
        });
      }

      // Store completed task info and show modal
      setCompletedTaskInfo({ week: parseInt(weekNumber), day: task.day });
      setCompletingTask(false);
      setShowShareModal(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete task');
      setCompletingTask(false);
    }
  };

  const handleCloseShareModal = () => {
    setShowShareModal(false);
    navigate('/dashboard');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Sparkles className="w-16 h-16 text-primary animate-pulse mx-auto mb-4" />
          <p className="text-secondary-700 text-lg">Loading week...</p>
        </div>
      </div>
    );
  }

  // Get hero image for this week
  const heroImage = weekHeroImages[parseInt(weekNumber)];

  return (
    <div className="min-h-screen bg-background" data-testid="week-view">
      {/* Navigation */}
      <Navigation />
      
      {/* Hero Image Section */}
      {heroImage && (
        <div className="relative w-full h-[300px] sm:h-[400px] lg:h-[450px] overflow-hidden" data-testid="week-hero-image">
          <img 
            src={heroImage.url} 
            alt={heroImage.alt}
            className="w-full h-full object-cover object-top"
          />
          {/* Gradient overlay for text readability */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
          
          {/* Back button positioned over hero */}
          <div className="absolute top-4 left-4 sm:left-6">
            <Button
              data-testid="back-to-dashboard-button"
              onClick={() => navigate('/dashboard')}
              variant="ghost"
              className="bg-white/90 hover:bg-white text-secondary-700 hover:text-primary shadow-md"
            >
              <ArrowLeft className="mr-2 w-5 h-5" /> Back to Dashboard
            </Button>
          </div>
          
          {/* Week info overlay on hero */}
          <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8 text-white">
            <div className="container mx-auto">
              <span className="inline-block px-4 py-1 bg-white/20 backdrop-blur-sm text-white rounded-full text-sm font-semibold mb-3 border border-white/30">
                Week {week.week_number}
              </span>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold mb-2 drop-shadow-lg">
                {week.theme}
              </h1>
              <p className="text-base sm:text-lg text-white/90 max-w-2xl drop-shadow-md">{week.description}</p>
            </div>
          </div>
        </div>
      )}
      
      {/* Fallback Header (if no hero image) */}
      {!heroImage && (
        <header className="bg-white shadow-sm border-b border-primary/10">
          <div className="container mx-auto px-6 py-6">
            <Button
              data-testid="back-to-dashboard-button-fallback"
              onClick={() => navigate('/dashboard')}
              variant="ghost"
              className="mb-4 text-secondary-700 hover:text-primary"
            >
              <ArrowLeft className="mr-2 w-5 h-5" /> Back to Dashboard
            </Button>
            <div>
              <span className="inline-block px-4 py-1 bg-primary/10 text-primary rounded-full text-sm font-semibold mb-3">
                Week {week.week_number}
              </span>
              <h1 className="text-4xl font-serif font-bold text-secondary-900 mb-3">
                {week.theme}
              </h1>
              <p className="text-lg text-secondary-700 mb-4">{week.description}</p>
            </div>
          </div>
        </header>
      )}

      <main className="container mx-auto px-6 py-12">
        {/* Weekly Journaling Reminder */}
        <section className="mb-8" data-testid="weekly-journal-reminder">
          <div className="bg-gradient-to-br from-primary-50 to-secondary-50 rounded-3xl p-8 shadow-lg border-2 border-primary/30">
            <div className="flex items-start gap-4">
              <div className="bg-primary/20 p-3 rounded-full shrink-0">
                <Sparkles className="w-7 h-7 text-primary" />
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-3">Your Weekly Journaling Practice</h2>
                <p className="text-secondary-800 leading-relaxed mb-3">
                  This week, take at least <strong>5 minutes daily</strong> to journal about your ongoing journey. Reflect on your <strong>strengths</strong>, acknowledge your <strong>challenges</strong>, celebrate your <strong>accomplishments</strong>, and express <strong>gratitude</strong> for the progress you're making.
                </p>
                <p className="text-secondary-700 text-sm italic">
                  Journaling helps consolidate your learning, build self-awareness, and create a record of your beautiful transformation.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Why This Matters */}
        <section className="mb-12" data-testid="importance-section">
          <div className="bg-gradient-to-br from-accent-50 to-secondary-50 rounded-3xl p-8 shadow-lg border-2 border-accent/20">
            <div className="flex items-start gap-4 mb-4">
              <div className="bg-accent/20 p-3 rounded-full">
                <Info className="w-6 h-6 text-accent-700" />
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-3">Why This Matters</h2>
                <p className="text-secondary-800 leading-relaxed">{week.importance}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Daily Tasks - One Day at a Time */}
        <section data-testid="daily-tasks-section">
          <h2 className="text-3xl font-serif font-bold text-secondary-900 mb-8">Your Daily Journey</h2>
          <div className="grid gap-6">
            {week.daily_tasks.map((task) => {
              // Determine task status
              const weekNum = parseInt(weekNumber);
              const isCurrentDay = weekNum === currentWeek && task.day === currentDay;
              
              // Task is COMPLETED if:
              // - Viewing any past week (ALL days accessible)
              // - Current week: days before current day are completed
              // - Program is complete means all tasks are done
              const isPastWeek = weekNum < currentWeek;
              const isPastDay = weekNum === currentWeek && task.day < currentDay;
              const isAccessibleDay = weekNum === currentWeek && task.day <= currentDay;
              const isFutureDay = weekNum === currentWeek && task.day > currentDay;
              const isFutureWeek = weekNum > currentWeek;
              
              // Show as completed if it's a past task (not current day) or program complete
              // Note: Admin can access everything but that doesn't mean tasks are completed
              const isCompleted = isPastWeek || isPastDay || programComplete;
              
              // Only lock future content
              const isLocked = !isAdmin && (isFutureDay || isFutureWeek);
              
              return (
                <div
                  key={task.id}
                  data-testid={`task-day-${task.day}`}
                  className={`bg-white rounded-2xl p-8 shadow-md border-2 transition-all duration-300 ${
                    isLocked
                      ? 'border-gray-200 opacity-60'
                      : isCompleted
                        ? 'border-green-300 bg-green-50/30'
                        : 'border-primary/30 hover:border-primary hover:shadow-lg'
                  }`}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-4">
                      <span className={`inline-block px-3 py-1 rounded-full text-sm font-bold ${
                        isCompleted && !isLocked
                          ? 'bg-green-100 text-green-700'
                          : 'bg-primary/10 text-primary'
                      }`}>
                        Day {task.day}
                      </span>
                      {isLocked && (
                        <span className="flex items-center gap-1 text-gray-500 text-sm">
                          <Lock className="w-4 h-4" />
                          Locked
                        </span>
                      )}
                      {isCompleted && !isLocked && !isCurrentDay && (
                        <span className="flex items-center gap-1 text-green-600 text-sm font-medium">
                          <Check className="w-4 h-4" />
                          Completed
                        </span>
                      )}
                      {isCurrentDay && !isLocked && (
                        <span className="flex items-center gap-1 text-primary text-sm font-medium">
                          <Sparkles className="w-4 h-4" />
                          Today's Task
                        </span>
                      )}
                    </div>
                    <h3 className="text-2xl font-serif font-bold text-secondary-900 mb-3">{task.title}</h3>
                    <p className="text-secondary-700 mb-4 text-lg">{task.description}</p>
                    
                    {/* Task Benefit */}
                    <div className="bg-accent-50 rounded-xl p-4 border border-accent/20 mb-4">
                      <p className="text-sm font-semibold text-accent-800 mb-2">Health Benefit:</p>
                      <p className="text-secondary-700 leading-relaxed">{task.benefit}</p>
                    </div>

                    {/* Resources/Links Section */}
                    {task.resources && task.resources.length > 0 && (
                      <div className="bg-blue-50 rounded-xl p-4 border border-blue-200 mb-4" data-testid={`task-resources-day-${task.day}`}>
                        <p className="text-sm font-semibold text-blue-800 mb-3 flex items-center gap-2">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                          </svg>
                          Helpful Resources:
                        </p>
                        <ul className="space-y-2">
                          {task.resources.map((resource, idx) => {
                            const isInternalLink = resource.url && resource.url.startsWith('/');
                            return (
                            <li key={idx} className="flex items-start gap-2">
                              <span className="text-blue-400 mt-1">•</span>
                              <div>
                                {isInternalLink ? (
                                  <button
                                    onClick={() => navigate(resource.url)}
                                    className="text-blue-600 hover:text-blue-800 hover:underline font-medium text-left"
                                  >
                                    {resource.title}
                                  </button>
                                ) : (
                                  <a 
                                    href={resource.url} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                                  >
                                    {resource.title}
                                  </a>
                                )}
                                {resource.description && (
                                  <p className="text-sm text-stone-600 mt-0.5">{resource.description}</p>
                                )}
                                {resource.type && (
                                  <span className={`inline-block mt-1 px-2 py-0.5 text-xs rounded-full ${
                                    resource.type === 'video' ? 'bg-red-100 text-red-700' :
                                    resource.type === 'recipe' ? 'bg-green-100 text-green-700' :
                                    resource.type === 'article' ? 'bg-purple-100 text-purple-700' :
                                    resource.type === 'workout' ? 'bg-orange-100 text-orange-700' :
                                    resource.type === 'fashion' ? 'bg-pink-100 text-pink-700' :
                                    'bg-gray-100 text-gray-700'
                                  }`}>
                                    {resource.type}
                                  </span>
                                )}
                              </div>
                            </li>
                          )})}
                        </ul>
                      </div>
                    )}

                    {/* Daily Essentials Checklist - Show for accessible days */}
                    {!isLocked && (
                      <DailyChecklist weekNumber={weekNumber} dayNumber={task.day} />
                    )}

                    {/* Daily Journaling Reminder - Only for current day that can be completed */}
                    {isCurrentDay && canCompleteToday && (
                      <div className="bg-secondary-50 rounded-xl p-5 border-2 border-secondary/30 mb-6">
                        <div className="flex items-start gap-3">
                          <div className="bg-secondary/20 p-2 rounded-full shrink-0">
                            <Sparkles className="w-5 h-5 text-secondary" />
                          </div>
                          <div>
                            <p className="text-sm font-bold text-secondary-900 mb-2">Before Completing Today:</p>
                            <p className="text-secondary-800 text-sm leading-relaxed mb-2">
                              Take 5+ minutes to journal about your journey. Reflect on:
                            </p>
                            <ul className="text-sm text-secondary-700 space-y-1 ml-4">
                              <li>• Your <strong>strengths</strong> showing through</li>
                              <li>• <strong>Challenges</strong> you're facing</li>
                              <li>• <strong>Accomplishments</strong> you've achieved</li>
                              <li>• What you're <strong>grateful</strong> for today</li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Complete Button at the end of each day */}
                    <div className="mt-6 pt-6 border-t border-gray-100">
                      {isCompleted && !isLocked && !isCurrentDay ? (
                        <div className="text-center">
                          <div className="flex items-center justify-center gap-2 text-green-600 font-medium mb-2">
                            <Check className="w-6 h-6" />
                            <span className="text-lg">Day {task.day} Completed!</span>
                          </div>
                          <p className="text-sm text-stone-500 italic">You can revisit this task anytime to review the goal and resources.</p>
                        </div>
                      ) : isAdmin && !isLocked ? (
                        // Admin can complete any unlocked task
                        <Button
                          data-testid={`complete-task-button-day-${task.day}`}
                          onClick={() => handleCompleteTask(task)}
                          disabled={completingTask}
                          className="w-full bg-purple-600 hover:bg-purple-700 text-white py-6 rounded-full shadow-lg transition-all duration-300 text-lg font-semibold hover:scale-[1.02]"
                        >
                          <Check className="mr-2 w-5 h-5" />
                          {completingTask ? 'Completing...' : `Complete Day ${task.day} (Admin)`}
                        </Button>
                      ) : isCurrentDay && canCompleteToday && !isLocked ? (
                        <Button
                          data-testid={`complete-task-button-day-${task.day}`}
                          onClick={() => handleCompleteTask(task)}
                          disabled={completingTask}
                          className="w-full bg-primary hover:bg-primary/90 text-white py-6 rounded-full shadow-lg transition-all duration-300 text-lg font-semibold hover:scale-[1.02]"
                        >
                          <Check className="mr-2 w-5 h-5" />
                          {completingTask ? 'Completing...' : `Complete Day ${task.day}`}
                        </Button>
                      ) : isCurrentDay && !canCompleteToday ? (
                        <div className="bg-amber-50 rounded-xl p-4 border-2 border-amber-200 text-center">
                          <div className="flex items-center justify-center gap-2 text-amber-700 font-medium mb-2">
                            <Clock className="w-5 h-5" />
                            <span>Today's task unlocks tomorrow</span>
                          </div>
                          <p className="text-sm text-amber-600">
                            Great job completing today! Come back tomorrow to continue your journey. 🌸
                          </p>
                        </div>
                      ) : isLocked ? (
                        <div className="flex items-center justify-center gap-2 text-gray-400 font-medium">
                          <Lock className="w-5 h-5" />
                          <span>Complete previous days to unlock</span>
                        </div>
                      ) : (
                        <div className="text-center">
                          <div className="flex items-center justify-center gap-2 text-green-600 font-medium mb-2">
                            <Check className="w-6 h-6" />
                            <span className="text-lg">Day {task.day} Completed!</span>
                          </div>
                          <p className="text-sm text-stone-500 italic">You can revisit this task anytime to review the goal and resources.</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Motivation */}
        <section className="mt-12">
          <div className="bg-gradient-to-br from-primary/10 to-secondary/10 rounded-3xl p-8 text-center">
            <Sparkles className="w-12 h-12 text-primary mx-auto mb-4" />
            <h3 className="text-2xl font-serif font-bold text-secondary-900 mb-3">
              One Day at a Time
            </h3>
            <p className="text-secondary-700 text-lg max-w-2xl mx-auto">
              Focus on today's task. Small, consistent steps lead to lasting transformation. Your garden grows with each day you complete!
            </p>
          </div>
        </section>
      </main>

      {/* Share Achievement Modal */}
      <ShareAchievementModal
        isOpen={showShareModal}
        onClose={handleCloseShareModal}
        weekNumber={completedTaskInfo.week}
        dayNumber={completedTaskInfo.day}
        isFirstDay={completedTaskInfo.week === 1 && completedTaskInfo.day === 1}
        isLastDay={completedTaskInfo.week === 12 && completedTaskInfo.day === 7}
      />
    </div>
  );
}

export default WeekView;